import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { PawPrint } from 'lucide-react';

const AuthPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'owner',
    organization_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const response = await axios.post(endpoint, payload);
      
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      onLogin(response.data.token, response.data.user);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{
      backgroundImage: 'linear-gradient(135deg, #1a4d2e 0%, #143d24 100%)'
    }}>
      <div data-testid="auth-form" className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md m-4">
        <div className="flex items-center justify-center gap-2 mb-8">
          <PawPrint className="w-8 h-8 text-primary" />
          <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Pet Registry</span>
        </div>

        <h2 className="text-3xl font-bold mb-6 text-center">{isLogin ? 'Welcome Back' : 'Create Account'}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  data-testid="name-input"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                  className="h-11"
                />
              </div>

              <div>
                <Label htmlFor="role">I am a...</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="role-select" className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Pet Owner</SelectItem>
                    <SelectItem value="shelter">Animal Shelter</SelectItem>
                    <SelectItem value="breeder">Breeder</SelectItem>
                    <SelectItem value="vet">Veterinarian</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(formData.role === 'shelter' || formData.role === 'breeder' || formData.role === 'vet') && (
                <div>
                  <Label htmlFor="organization_name">Organization Name</Label>
                  <Input
                    data-testid="organization-input"
                    id="organization_name"
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    className="h-11"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  data-testid="phone-input"
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-11"
                />
              </div>
            </>
          )}

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              data-testid="email-input"
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="h-11"
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              data-testid="password-input"
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              className="h-11"
            />
          </div>

          <Button data-testid="submit-btn" type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 h-11 rounded-full text-lg">
            {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            data-testid="toggle-auth-btn"
            onClick={() => setIsLogin(!isLogin)}
            className="text-primary hover:underline"
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
